"use client"
import { useDateTimeFormatter } from "@opensea/i18n"
import { CalendarToday } from "../../icons"
import { classNames } from "../../utils"
import { Button, type ButtonProps } from "../Button"
import { Calendar, type CalendarProps } from "../Calendar/Calendar"
import { Popover } from "../Popover"

export type DatePickerProps = CalendarProps & {
  placeholder?: string
  format?: (date: Date | undefined) => string
  size?: ButtonProps["size"]
  error?: boolean
  portal?: boolean
  overrides?: {
    Button?: Partial<ButtonProps>
  }
}

export function DatePicker({
  overrides,
  selected,
  onSelect,
  placeholder = "Select a date",
  format: formatFn,
  size = "md",
  disabled,
  error,
  portal,
  ...props
}: DatePickerProps) {
  const defaultFormat = useDateTimeFormatter()

  const formattedSelected = formatFn
    ? formatFn(selected)
    : defaultFormat(selected, { display: "date-numeric" })

  const disabledButton = typeof disabled === "boolean" && disabled

  return (
    <Popover
      className="min-h-[344px]"
      content={
        <Calendar
          disabled={disabled}
          initialFocus
          onSelect={onSelect}
          selected={selected}
          {...props}
        />
      }
      portal={portal}
    >
      <Button
        className={classNames(
          selected && "font-mono",
          error && "!border-error-1 border",
        )}
        disabled={disabledButton}
        icon={CalendarToday}
        iconSide="right"
        size={size}
        variant="secondary"
        {...overrides?.Button}
      >
        {selected ? formattedSelected : placeholder}
      </Button>
    </Popover>
  )
}
